import axios, { type AxiosError, type AxiosInstance } from "axios";

// Augment axios so a caller can flag a request as root-scoped (addressed at
// `{apiBaseUrl}/root`) instead of the default tenant scope.
declare module "axios" {
  interface AxiosRequestConfig {
    /** When true, the request targets the root scope instead of a tenant. */
    isRootRequest?: boolean;
  }
}

const MIME_TYPE_JSON_LD = "application/ld+json";

/**
 * Runtime dependencies the client resolves on every request, so identity and
 * tenant can change without rebuilding the client. A later OIDC integration
 * supplies the real implementations; tests provide them explicitly.
 */
export interface ApiClientOptions {
  /** API base URL without a trailing slash, e.g. `https://api.rumbledirect.com`. */
  readonly apiBaseUrl: string;
  /** The current bearer access token, or `null` when unauthenticated. */
  readonly getAccessToken: () => string | null;
  /** The current tenant (affiliate) id, or `null` when unknown. */
  readonly getAffiliateId: () => string | null;
  /** Called when the API answers 401 so the app can clear its session. */
  readonly onUnauthorized: () => void;
}

/**
 * Creates the shared axios instance for the RumbleDirect API.
 *
 * The instance negotiates JSON-LD, attaches the bearer token when present, and
 * routes requests to the tenant scope (`/affiliates/{id}`) or, when a request
 * opts in via `isRootRequest`, to the root scope (`/root`). A 401 response
 * triggers `onUnauthorized` before the error is re-thrown. Response payloads are
 * passed through unchanged, so callers (and the generated client) receive the
 * API's JSON-LD shape exactly as typed.
 */
export function createApiClient(options: ApiClientOptions): AxiosInstance {
  const { apiBaseUrl, getAccessToken, getAffiliateId, onUnauthorized } =
    options;

  const instance = axios.create({
    headers: {
      Accept: MIME_TYPE_JSON_LD,
      "Content-Type": MIME_TYPE_JSON_LD,
    },
  });

  instance.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token !== null) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.isRootRequest === true) {
      config.baseURL = `${apiBaseUrl}/root`;
      return config;
    }

    const affiliateId = getAffiliateId();
    if (affiliateId === null) {
      throw new Error(
        "Cannot perform a tenant-scoped request without an affiliate id.",
      );
    }

    config.baseURL = `${apiBaseUrl}/affiliates/${affiliateId}`;
    return config;
  });

  instance.interceptors.response.use(undefined, (error: AxiosError) => {
    if (error.response?.status === 401) {
      onUnauthorized();
    }
    return Promise.reject(error);
  });

  return instance;
}
